"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQCornerButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import { iconMap } from "@/lib/iconMap";

const cornerButtonVariants = cva("corner-button", {
  variants: {
    type: {
      default: "corner-cta corner-button-default",
      dark: "corner-cta corner-button-dark",
      white: "corner-cta corner-button-white",
      clearOutline: "corner-cta corner-clear-outline",
      launchWhite: "launch-button launch-button-white",
      launchBlack: "launch-button launch-button-black",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      videoPopup: "",
      none: "",
    },
  },
  defaultVariants: {
    type: "default",
    action: "none",
  },
});

export interface CornerButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">,
    VariantProps<typeof cornerButtonVariants> {
  asChild?: boolean;
  htmlType?: "button" | "submit" | "reset";
}

const CornerButton = React.forwardRef<HTMLButtonElement, CornerButtonProps>(
  ({ className, type, action, asChild = false, htmlType = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn(
          "group",
          cornerButtonVariants({ type, action }),
          className,
        )}
        data-type={type}
        data-action={action}
        ref={ref}
        type={htmlType}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
CornerButton.displayName = "CornerButton";

export interface CTACornerButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQCornerButton;
  /**
   * Only used by the launch variants: "sm" is the compact header version, "md" the default on
   * phones and the smaller step-card version from lg up (thank-you page), "block" fills its
   * container on every breakpoint (popup).
   */
  size?: "sm" | "md" | "default" | "block";
  /** Only used by the launch variants: shorter label shown below the lg breakpoint. */
  mobileLabel?: string;
  /** Only used by the launch variants: keeps the size but stretches the button to its container. */
  fullWidth?: boolean;
}

type CornerButtonType = NonNullable<GROQCornerButton["type"]>;

/** Play square of the launch variants (Figma: TradeMania 2.0 · "Jetzt Gratis-Platz sichern"). */
function LaunchButtonIcon({ className }: { className: string }) {
  return (
    <span className={cn("launch-button-icon", className)} aria-hidden>
      <svg viewBox="0 0 12 18" fill="currentColor">
        <path d="M12 9L0 0V18L12 9Z" />
      </svg>
    </span>
  );
}

const CTACornerButton = React.forwardRef<
  HTMLButtonElement,
  CTACornerButtonProps
>(({ className, data, type: _type, size = "default", mobileLabel, fullWidth, ...props }, ref) => {
  const { title } = useCTAWrapper(data?.cta);
  const cleanedData = stegaClean(data ?? {}) ?? null;

  const buttonType = (cleanedData?.type ?? "default") as CornerButtonType;

  const hasIcon = cleanedData?.cta?.icon ?? "none";
  const icon = iconMap[hasIcon as keyof typeof iconMap];

  const action = cleanedData?.cta?.action ?? null;
  const label = data?.cta ? (title ?? "Please add a title") : "Please select a CTA";

  if (buttonType === "launchWhite" || buttonType === "launchBlack") {
    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn(
          "relative z-10 inline-block",
          size === "sm" && "w-auto",
          (size === "default" || size === "md") && "w-full lg:w-auto",
          // md: overrides the popup trigger's md:w-auto
          (size === "block" || fullWidth) && "w-full md:w-full",
          className,
        )}
      >
        <CornerButton
          className={cn(
            size === "sm" && "launch-button-sm",
            size === "md" && "launch-button-md",
            size === "block" && "launch-button-block",
            fullWidth && "launch-button-full",
          )}
          type={buttonType}
          action={action ?? "none"}
          ref={ref}
          {...props}
        >
          <span className="launch-button-row">
            <LaunchButtonIcon className="launch-button-icon-hover" />
            <span className="launch-button-text">
              {mobileLabel ? (
                <>
                  <span className="lg:hidden">{mobileLabel}</span>
                  <span className="hidden lg:inline">{label}</span>
                </>
              ) : (
                label
              )}
            </span>
            <LaunchButtonIcon className="launch-button-icon-main" />
          </span>
        </CornerButton>
      </CTAWrapper>
    );
  }

  return (
    <CTAWrapper
      cta={cleanedData?.cta}
      className={cn(
        "inline-block w-full min-w-[30rem] sm:w-auto relative z-10",
        className,
      )}
    >
      <CornerButton
        className="relative w-full group [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)]"
        type={buttonType}
        action={action ?? "none"}
        ref={ref}
        {...props}
      >
        <div className="corner-button-text relative">
          <div className="corner-button-text-initial transition-all duration-300">
            {data?.cta
              ? (title ?? "Please add a title")
              : "Please select a CTA"}
          </div>
          <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
            {data?.cta
              ? (title ?? "Please add a title")
              : "Please select a CTA"}
          </div>
        </div>

        {icon && (
          <>
            <div className="corner-button-divider h-[4rem]" />

            <div className="corner-button-icon relative [&_svg]:w-full overflow-clip">
              <div className="corner-button-icon-initial transition-all duration-300 w-[3rem] h-[3rem]">
                {icon}
              </div>
              <div className="corner-button-icon-hover absolute top-[2.25rem] -left-[2.25rem] transition-all duration-300 pointer-events-none w-[3rem] h-[3rem]">
                {icon}
              </div>
            </div>
          </>
        )}
      </CornerButton>
    </CTAWrapper>
  );
});
CTACornerButton.displayName = "CTACornerButton";

export { CornerButton, cornerButtonVariants, CTACornerButton };
